/**
 * B3 (Task 15, spec §12b pct. 3): „Descarcă exerciții PDF" descarca REAL, si pe desktop.
 *
 * De ce nu `window.open(pdfManualUrl)`: PDF-ul sta pe contul de stocare comun, fara CORS pentru noi si
 * fara `Content-Disposition: attachment`, deci pe desktop browserul il DESCHIDE intr-un tab, nu il
 * salveaza. Backendul (`GET /api/lessons/{id}/exercitii-pdf`) il trimite ca atasament, din containerul
 * nostru; noi il luam ca Blob si il salvam printr-un `<a download>`.
 *
 * 🔴 Fix round 2 (review T15): backendul descarca DOAR un URL din containerul nostru, folderul
 * `lesson-pdfs/`, cu un nume dintr-un singur segment (`util/BlobPropriu`). Orice alt URL (Cloudinary,
 * nume cu spatii/paranteze) primea 409 si abia DUPA round-trip se chema `window.open` — pe care Safari
 * (iOS/macOS) il blocheaza cand vine dupa o cerere asincrona: omul nu vedea nimic. De aceea URL-ul se
 * clasifica pe client INAINTE de orice cerere, cu aceeasi regula ca serverul: ce n-ar trece acolo se
 * deschide SINCRON, in gestul click-ului, exact ca inainte de B3. Ce esueaza dupa cerere (404, retea)
 * NU mai deschide tab (ar fi blocat): intoarce `'esuat'`, iar apelantul arata un link „Deschide PDF-ul"
 * pe care omul il apasa el.
 *
 * Dependintele (cererea, salvarea, tabul) se injecteaza: vitest ruleaza pe `environment: 'node'`, fara
 * DOM. Semnatura publica e `descarcaExercitiiPdf(lessonId, titlu, pdfManualUrl)` — refolosita si pe
 * alte tipuri de lectie (CLASS_EXERCISE).
 */
import { descarcaExercitiiPdf as descarcaExercitiiPdfSdk } from '@/api/generated/sdk.gen';
import { handleResponse } from '@/core/api/response-handler';
import { numeFisier } from '@/domains/auth/utils/protocol-descarcare';

export interface DepDescarcare {
  /** Cere PDF-ul de la backend; arunca la orice eroare. */
  cere: (lessonId: string) => Promise<{ blob: Blob; dispozitie: string | null }>;
  /** Salveaza blob-ul ca fisier, cu numele dat. */
  salveaza: (blob: Blob, nume: string) => void;
  /** Deschide URL-ul original intr-un tab nou (doar sincron, in gestul click-ului). */
  deschideTab: (url: string) => void;
}

/** `'esuat'` = apelantul arata un link direct spre `pdfManualUrl` (vezi `EroareDescarcarePdf`). */
export type RezultatDescarcare = 'descarcat' | 'tab-nou' | 'esuat';

const NUME_GENERIC = 'exercitii.pdf';

/**
 * Prefixul pe care il accepta backendul: `AzureBlobStorageService.getContainerUrl()` (contul si
 * containerul din `azure.storage.*`) + `AzureBlobFolder.LESSON_PDFS` („lesson-pdfs/"). Daca se schimba
 * contul/containerul, un URL nou ar cadea aici pe tab nou — degradare lina, nu eroare.
 */
const PREFIX_EXERCITII = 'https://dbeststorystorage.blob.core.windows.net/ecotic/lesson-pdfs/';
/** Ca `BlobPropriu.SAFE_BLOB_FILENAME`: un singur segment, fara `/`, `?`, `#`, spatii, paranteze. */
const NUME_BLOB_SIGUR = /^[A-Za-z0-9._-]+$/;

/**
 * Oglinda garzii din backend (`BlobPropriu.numeFisier` + `trim()` din `ExercitiiPdfService`):
 * decodare procent (`URLDecoder`: si `+` → spatiu), prefixul nostru, apoi un singur segment sigur,
 * fara `..`. `true` = serverul il poate descarca.
 */
export function esteUrlExercitiiPropriu(url: string | null | undefined): boolean {
  if (!url) return false;
  let decodat: string;
  try {
    decodat = decodeURIComponent(url.trim().replace(/\+/g, ' '));
  } catch {
    return false;
  }
  if (!decodat.startsWith(PREFIX_EXERCITII)) return false;
  const rest = decodat.slice(PREFIX_EXERCITII.length);
  return rest !== '' && !rest.includes('..') && NUME_BLOB_SIGUR.test(rest);
}

/** Numele de rezerva, din titlul activitatii: fara diacritice si fara caractere interzise in nume. */
export function numePdfDinTitlu(titlu: string): string {
  const curat = titlu
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[–—]/g, '-')
    .replace(/[\\/:*?"<>|“”„]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return curat ? `${curat}.pdf` : NUME_GENERIC;
}

async function cereDinBackend(lessonId: string): Promise<{ blob: Blob; dispozitie: string | null }> {
  const raspuns = await descarcaExercitiiPdfSdk({ path: { id: lessonId } });
  const blob = handleResponse(raspuns) as Blob;
  const antete = (raspuns as { headers?: Record<string, unknown> }).headers;
  const dispozitie = antete?.['content-disposition'];
  return { blob, dispozitie: typeof dispozitie === 'string' ? dispozitie : null };
}

function salveazaInBrowser(blob: Blob, nume: string): void {
  const url = URL.createObjectURL(blob);
  const legatura = document.createElement('a');
  legatura.href = url;
  legatura.download = nume;
  document.body.appendChild(legatura);
  legatura.click();
  document.body.removeChild(legatura);
  // Revocat dupa ce browserul a apucat sa porneasca descarcarea.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function deschideInTabNou(url: string): void {
  window.open(url, '_blank', 'noopener');
}

const DEP_IMPLICITE: DepDescarcare = {
  cere: cereDinBackend,
  salveaza: salveazaInBrowser,
  deschideTab: deschideInTabNou,
};

/**
 * Dependintele implicite, cu alta sursa pentru PDF: pagina publica a colaboratorului il cere prin
 * linkul ei (`/api/collab/{token}/activities/{id}/exercitii-pdf`), nu prin ruta autentificata.
 */
export function depDescarcareCu(cere: DepDescarcare['cere']): DepDescarcare {
  return { ...DEP_IMPLICITE, cere };
}

async function descarcaPrinBackend(lessonId: string, titlu: string, dep: DepDescarcare): Promise<RezultatDescarcare> {
  try {
    const { blob, dispozitie } = await dep.cere(lessonId);
    // Un corp gol nu e un PDF.
    if (!blob || blob.size === 0) return 'esuat';
    dep.salveaza(blob, numeFisier(dispozitie, numePdfDinTitlu(titlu)));
    return 'descarcat';
  } catch {
    return 'esuat';
  }
}

/**
 * NU e `async` intentionat: ramura „tab nou" trebuie sa ruleze sincron, in stiva click-ului.
 */
export function descarcaExercitiiPdf(
  lessonId: string,
  titlu: string,
  pdfManualUrl: string | null | undefined,
  dep: DepDescarcare = DEP_IMPLICITE
): Promise<RezultatDescarcare> {
  if (!pdfManualUrl) return Promise.resolve('esuat');
  if (!esteUrlExercitiiPropriu(pdfManualUrl)) {
    dep.deschideTab(pdfManualUrl);
    return Promise.resolve('tab-nou');
  }
  return descarcaPrinBackend(lessonId, titlu, dep);
}
